import { PosEvent, type SaleContext, type SaleItem } from '@core/bus/events'
import type { Writeoff } from '@core/db/models'
import type { Plugin, PluginContext, HandlerResult, MarkingIssue } from '@core/bus/types'
import type { MarkResolver, MarkingInspection, MarkingProvider } from '@core/marking/types'
import {
  extractGtinFromDataMatrix,
  looksLikeDataMatrix,
  markingStatusText,
  normalizeMarkingCode,
  parseMarkingCode
} from './datamatrix'
import { CzVerifyQueue } from './verify'
import { CzWithdrawalService } from './withdrawal'

// Контракт ввода марок теперь в ядре; реэкспорт держим на время перехода,
// чтобы существующие импорты UI не ломались.
export type { MarkRequest, MarkResolver } from '@core/marking/types'
import { debugLog } from '@core/utils/log'

/**
 * Публичный API плагина Честного Знака.
 *
 * Реализует порт ядра `MarkingProvider` — поэтому UI и SaleService работают
 * с ним через `core.marking`, не зная про ЧЗ. `drainOfflineBuffer` остаётся
 * спецификой модуля.
 */
export interface RuCzPlugin extends Plugin, MarkingProvider {
  /** Снять накопленные оффлайн-марки по чеку (для фискального накопителя). */
  drainOfflineBuffer(receiptId: string): string[]
}

/** Имитация ответа True API по коду маркировки. */
interface MarkingVerdict {
  valid: boolean
  reason?: string
}

/**
 * Плагин «Честный Знак» (ЧЗ).
 *
 * Подписывается на PRE_SALE. Если в чеке есть позиция, подлежащая маркировке
 * (requiresMarking), но без кода DataMatrix, плагин запрашивает ввод марки
 * через UI-резолвер и:
 *  - онлайн: имитирует проверку кода в True API;
 *  - оффлайн: складывает код в буфер чека для фискального накопителя
 *    (отложенная отправка при восстановлении связи, окно 72 ч).
 * Если марку получить не удалось (кассир отменил или код невалиден) —
 * возвращает veto, блокируя продажу.
 *
 * Важно: сам факт «требует маркировки» определяется полем requiresMarking,
 * которое проставляет эвристический фильтр молочки при импорте/создании.
 */
export function createRuCzPlugin(): RuCzPlugin {
  let markResolver: MarkResolver | null = null
  let online = typeof navigator !== 'undefined' ? navigator.onLine : true
  // Модуль включён по умолчанию; директор может отключить его в админке.
  let enabled = true
  /** Буфер оффлайн-марок: receiptId → список DataMatrix. */
  const offlineBuffer = new Map<string, string[]>()
  /** Очередь фоновой проверки кодов через backend (65.12.4). */
  let queue: CzVerifyQueue | null = null
  /** Очередь вывода кодов из оборота (Stage D). */
  let withdrawals: CzWithdrawalService | null = null

  /**
   * 65.12.1: локальная структурная проверка кода маркировки.
   *
   * True API здесь вызывать нельзя (лимит 2 с и прямой запрет для ККТ при
   * рознице), поэтому проверяем структуру: AI 01/21, GS, контрольный разряд
   * GTIN, криптохвост. Статус кода в обороте даёт ККТ (ЕСМ/ЛМ ЧЗ) — 65.12.2+.
   */
  function validateMarkingStructure(datamatrix: string): MarkingVerdict {
    const parsed = parseMarkingCode(datamatrix)
    if (!parsed.isValid) {
      return { valid: false, reason: markingStatusText(parsed) }
    }
    return { valid: true }
  }

  async function handlePreSale(sale: SaleContext): Promise<HandlerResult> {
    // Модуль отключён директором — не запрашиваем марки и не блокируем продажу.
    if (!enabled) {
      return { ok: true }
    }
    const needMarks = sale.items.filter(
      (i) =>
        isMarkable(i) &&
        !i.datamatrix &&
        // Кассир уже осознанно пропустил/подтвердил invalid — не спрашиваем снова.
        !i.markingSkipped &&
        !i.markingInvalid
    )
    if (needMarks.length === 0) {
      return { ok: true }
    }

    if (!markResolver) {
      return {
        ok: false,
        vetoReason:
          'Требуется маркировка «Честный Знак», но интерфейс ввода марки недоступен',
        markingIssues: needMarks.map((i) => ({
          lineId: i.lineId,
          name: i.name,
          kind: 'missing' as const,
          reason: 'Интерфейс ввода марки недоступен'
        }))
      }
    }

    const provided = await markResolver({
      receiptId: sale.receiptId,
      items: needMarks.map((i) => ({ lineId: i.lineId, name: i.name, gtin: i.gtin }))
    })

    if (!provided) {
      // Не «немое» вето: UI должен предложить «Пропустить без кода» (по праву)
      // или убрать позиции, а не просто показать тост.
      return {
        ok: false,
        vetoReason: 'Ввод кода маркировки отменён кассиром',
        markingIssues: needMarks.map((i) => ({
          lineId: i.lineId,
          name: i.name,
          kind: 'missing' as const,
          reason: 'Код маркировки не введён'
        }))
      }
    }

    // 65.10: собираем ВСЕ проблемы, а не падаем на первой. UI покажет список
    // и предложит «Убрать позицию» / «Пропустить без кода» (по праву).
    const issues: MarkingIssue[] = []
    for (const item of needMarks) {
      const key = item.lineId ?? item.name
      const code = provided[key] ?? provided[item.name]
      if (!code) {
        issues.push({
          lineId: item.lineId,
          name: item.name,
          kind: 'missing',
          reason: 'Код маркировки не введён'
        })
        continue
      }

      // 65.12.1: структура кода проверяется локально — и в онлайне, и в офлайне.
      // Статус в обороте проверяет ККТ (ЕСМ/ЛМ ЧЗ), а не наш backend.
      const verdict = validateMarkingStructure(code)
      if (!verdict.valid) {
        issues.push({
          lineId: item.lineId,
          name: item.name,
          code,
          kind: 'invalid',
          reason: verdict.reason ?? 'код недействителен'
        })
        continue
      }
      // Нормализуем: убираем переводы строк сканера; регистр и GS сохраняются.
      const normalizedCode = normalizeMarkingCode(code)
      if (online) {
        debugLog(
          `[ru-cz] структура марки для «${item.name}» в порядке (статус в обороте проверит ККТ)`
        )
      } else {
        const buf = offlineBuffer.get(sale.receiptId) ?? []
        buf.push(normalizedCode)
        offlineBuffer.set(sale.receiptId, buf)
        debugLog(`[ru-cz] offline: марка для «${item.name}» сохранена в буфер чека`)
      }

      // Прописываем код в позицию, чтобы он попал в чек/фискальный накопитель.
      item.datamatrix = normalizedCode
    }

    if (issues.length > 0) {
      return {
        ok: false,
        vetoReason: issues.map((i) => `«${i.name}»: ${i.reason}`).join('; '),
        markingIssues: issues
      }
    }

    return { ok: true }
  }

  return {
    id: 'ru-cz',
    country: 'RU',

    init(ctx: PluginContext): void {
      debugLog('[ru-cz] init')
      // Автослежение за статусом сети, если доступно в окружении.
      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => {
          online = true
          debugLog('[ru-cz] сеть: online')
          // Вернулась связь — досылаем накопленные проверки.
          void queue?.flush().catch((error) => console.warn('[ru-cz] flush не удался:', error))
        })
        window.addEventListener('offline', () => {
          online = false
          debugLog('[ru-cz] сеть: offline')
        })
      }
      ctx.bus.on(PosEvent.PRE_SALE, (sale) => {
        // Возврат кодов маркировки в оборот — вне объёма 65.5: не блокируем
        // возврат проверкой ЧЗ (SaleService и так не вызывает PRE_SALE для
        // возврата, это защита в глубину).
        if (sale.settlementType === 'income_return') return
        return handlePreSale(sale)
      })

      // 65.12.4: фоновая проверка кодов через backend + восстановление очереди.
      queue = new CzVerifyQueue({
        db: ctx.db,
        cloud: ctx.cloud,
        onStatus: (verdict) => {
          void ctx.bus.emit(PosEvent.MARKING_STATUS_UPDATED, {
            code: verdict.code,
            status: verdict.status,
            reason: verdict.reason
          })
        },
        logger: (message) => console.warn(message)
      })
      withdrawals = new CzWithdrawalService(ctx.cloud)
      queue.start(60_000)
      void queue
        .restore()
        .then((count) => {
          if (count > 0) debugLog(`[ru-cz] восстановлено чеков без статуса ЧЗ: ${count}`)
        })
        .catch((error) => console.warn('[ru-cz] restore не удался:', error))

      const enqueueWithdrawals = async (sale: SaleContext): Promise<void> => {
        if (!withdrawals) return
        const receipt = await ctx.db.receipts
          .where('receiptId')
          .equals(sale.receiptId)
          .first()
        // Только оплаченный чек: фискализированный, non-fiscal, hold или
        // ожидающий дофискализации. Для `offline_pending`/`marking_review`
        // ставим OFD-сверку сразу: она безопасна (только чтение) и не даст
        // потерять вывод, когда чек позже станет `fiscalized`.
        if (
          !receipt ||
          (receipt.status !== 'fiscalized' &&
            receipt.status !== 'offline_pending' &&
            receipt.status !== 'marking_review' &&
            receipt.status !== 'non_fiscal')
        ) {
          return
        }
        const mode = receipt.status === 'non_fiscal' ? 'true_api' : 'ofd'
        for (const item of sale.items) {
          const code = item.datamatrix?.trim()
          if (!code) continue
          try {
            await withdrawals.enqueue({
              receiptId: sale.receiptId,
              lineId: item.lineId,
              code,
              productCostCents: Math.round(item.price * 100),
              mode,
              reason: 'sale'
            })
          } catch (error) {
            console.warn('[ru-cz] не удалось поставить вывод кода в очередь:', error)
          }
        }
      }

      const enqueueWriteoff = async (writeoff: Writeoff): Promise<void> => {
        if (!enabled || !withdrawals) return
        const reason =
          writeoff.reasonCode === 'supplier_return' ? 'supplier_return' : 'writeoff'
        for (const item of writeoff.items) {
          const code = item.datamatrix?.trim()
          if (!code) continue
          try {
            await withdrawals.enqueue({
              receiptId: writeoff.receiptId ?? null,
              sourceId: writeoff.writeoffId,
              lineId: item.lineId,
              code,
              productCostCents: Math.round(item.price * 100),
              mode: 'true_api',
              reason
            })
          } catch (error) {
            console.warn('[ru-cz] не удалось поставить списание в очередь вывода:', error)
          }
        }
      }

      ctx.bus.on(PosEvent.SALE_COMPLETED, (sale) => {
        // Возврат возвращает код в оборот — проверять нечего.
        if (sale.settlementType === 'income_return') return
        const codes = sale.items
          .map((item) => item.datamatrix?.trim())
          .filter((code): code is string => Boolean(code))
        if (codes.length > 0) void queue?.attachReceipt(sale.receiptId, codes)
        void enqueueWithdrawals(sale)
      })

      // Списание и возврат поставщику — отдельный True API-документ.
      ctx.bus.on(PosEvent.WRITEOFF_COMPLETED, (writeoff) => {
        void enqueueWriteoff(writeoff)
      })
    },

    setMarkResolver(resolver: MarkResolver | null): void {
      markResolver = resolver
    },

    setOnline(value: boolean): void {
      online = value
    },

    isOnline(): boolean {
      return online
    },

    retryVerification(codes: string[]): void {
      queue?.retry(codes)
    },

    setEnabled(value: boolean): void {
      enabled = value
      // Выключили модуль — не ходим в контур и не пишем статусы.
      if (value) queue?.start(60_000)
      else queue?.stop()
      debugLog(`[ru-cz] модуль маркировки ${value ? 'включён' : 'отключён'}`)
    },

    isEnabled(): boolean {
      return enabled
    },

    // --- Порт ядра MarkingProvider ---------------------------------------
    inspect(raw: string): MarkingInspection {
      const parsed = parseMarkingCode(raw)
      return {
        normalized: parsed.normalized,
        acceptable: parsed.isValid,
        recognized: looksLikeDataMatrix(raw),
        reason: parsed.isValid ? undefined : markingStatusText(parsed),
        hint:
          !parsed.isValid && parsed.hasLowercaseSerial
            ? 'проверьте настройки регистра сканера'
            : undefined,
        note:
          parsed.isValid && parsed.hasLowercaseSerial
            ? 'Регистр серийного номера сохранён (требование ЧЗ)'
            : undefined
      }
    },

    looksLike(raw: string): boolean {
      return looksLikeDataMatrix(raw)
    },

    gtinOf(raw: string): string | null {
      return extractGtinFromDataMatrix(raw)
    },

    verify(codes: string[]): void {
      if (!enabled) return
      queue?.verify(codes)
    },


    drainOfflineBuffer(receiptId: string): string[] {
      const buf = offlineBuffer.get(receiptId) ?? []
      offlineBuffer.delete(receiptId)
      return buf
    },

    dispose(): void {
      queue?.stop()
      queue = null
    }
  }
}

/** Позиция подлежит маркировке? Пока единственный критерий — молочка (ЧЗ). */
function isMarkable(item: SaleItem): boolean {
  return Boolean(item.requiresMarking)
}

/** Готовый экземпляр плагина для регистрации в bootstrap. */
export const ruCzPlugin: RuCzPlugin = createRuCzPlugin()
